import React from "react";

// MUI
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

// Hooks
import { useAppSelector } from "../../../redux/hooks/redux-hooks";

// Utils
import { displayDateFormart } from "../../../utility/utils";

// Components
import Divider from "../../Divider";

export default function PreviewBidDetails() {
  const particularUserSBU = useAppSelector(
    (state) => state.sbu.particularUserSBU
  ); // setted while choosing sbu in basic info section
  const bidDetails = useAppSelector((state) => state.project.project_Object);
  const allBus = useAppSelector((state) => state.bu.all_BUs);
  const allSegmentLobs = useAppSelector(
    (state) => state.segmentLOB.all_SegmentLOBs
  );
  const allCustomers = useAppSelector((state) => state.customer.all_Customers);
  const allCustomerTypes = useAppSelector(
    (state) => state.customerType.all_CustomerTypes
  );
  const allContractTypes = useAppSelector(
    (state) => state.contractType.all_ContractTypes
  );
  const allFundingSources = useAppSelector(
    (state) => state.finance.all_Finances
  );
  const allTenderingBandwidths = useAppSelector(
    (state) => state.tenderingBandwidth.all_TenderingBandwidths
  );
  const allGeographies = useAppSelector(
    (state) => state.geography.all_Geographies
  );
  const allCompetitionIntensities = useAppSelector(
    (state) => state.competitionIntensity.all_CompetitionIntensities
  );

  const bu = allBus.find((rec) => rec.buid === bidDetails.buid);
  const segmentLob = allSegmentLobs.find(
    (rec) => rec.segmentlobid === bidDetails.segmentLobid
  );
  const customer = allCustomers.find(
    (rec) => rec.customerId === bidDetails.customerNameId
  );
  const customerType = allCustomerTypes.find(
    (rec) => rec.customerTypeId === customer?.customerTypeId
  );
  const contractType = allContractTypes.find(
    (rec) => rec.contractTypeId === bidDetails.contractTypeId
  );
  const fundingSource = allFundingSources.find(
    (rec) => rec.fundingSourceId === bidDetails.fundingSourceId
  );
  const tenderingBandwidth = allTenderingBandwidths.find(
    (rec) => rec.tenderingBandwidthId === bidDetails.tenderingBandwidthId
  );
  const geography = allGeographies.find(
    (rec) => rec.geographyId === bidDetails.geographyId
  );
  const competitionIntensity = allCompetitionIntensities.find(
    (rec) => rec.competitionIntensityId === bidDetails.competitionIntensityId
  );

  const renderColumnComponent = (
    columnHeader: string,
    columnText: string | undefined,
    xs: number = 6
  ) => {
    return (
      <Grid item xs={xs}>
        <Typography variant="subtitle2" fontWeight={"bold"}>
          {columnHeader}
        </Typography>
        <Typography variant="body2">{columnText}</Typography>
      </Grid>
    );
  };

  return (
    <Box pl="1em" pr="1em" pb="1em">
      <Typography variant="subtitle1">Basic Info Details</Typography>
      <Divider />
      <Grid container spacing={1} mb={2}>
        {renderColumnComponent("Project Name", bidDetails.projectName, 12)}
        {renderColumnComponent("SBG", particularUserSBU.sbgName)}
        {renderColumnComponent("SBU", particularUserSBU.sbuName)}
        {renderColumnComponent("BU/LOB", bu?.name)}
        {renderColumnComponent("Segment", segmentLob?.name)}
        {renderColumnComponent("Customer Name", customer?.name)}
        {renderColumnComponent(
          "Segment Classification",
          bidDetails.segmentClassification
        )}
        {renderColumnComponent(
          "Base Load",
          bidDetails.isBaseLoad ? "Yes" : "No"
        )}
        {renderColumnComponent(
          "Green Customer",
          bidDetails.isGreenListCustomer ? "Yes" : "No"
        )}
        {renderColumnComponent("Type of Customer", customerType?.name)}
        {renderColumnComponent("Brief scope", bidDetails.briefScope)}
        {renderColumnComponent(
          "Estimated value (Cr.)",
          bidDetails.estimatedValue.toLocaleString("en-IN")
        )}
        {renderColumnComponent("Contract Type", contractType?.name)}
        {contractType?.name === "Others" &&
          renderColumnComponent(
            "Other Contract Type",
            bidDetails.otherContractType && bidDetails.otherContractType
          )}
        {renderColumnComponent("Price Variation", bidDetails.isPriceVariation)}
        {renderColumnComponent(
          "Assess customer rating",
          bidDetails.assessCustomerRating
        )}
      </Grid>
      <Typography variant="subtitle1">Bid Details</Typography>
      <Divider />
      <Grid container spacing={1} mb={2}>
        {renderColumnComponent("Meets PQ requirement", bidDetails.isPqrequired)}
        {renderColumnComponent(
          "Select JV Partner Requirement",
          bidDetails.isJvpartnerRequired ? "Yes" : "No"
        )}
        {bidDetails.jvpartnerSuggestedName &&
          renderColumnComponent(
            "JV Partner Suggested Name",
            bidDetails.jvpartnerSuggestedName
          )}
        {renderColumnComponent(
          "Select Tech Partner Requirement",
          bidDetails.isTechPartnerRequired ? "Yes" : "No"
        )}
        {bidDetails.techPartnerSuggestedName &&
          renderColumnComponent(
            "Tech Partner Suggested Name",
            bidDetails.techPartnerSuggestedName
          )}
        {bidDetails.goFactorPercentage &&
          renderColumnComponent(
            "Go Factor (%)",
            bidDetails.goFactorPercentage.toString()
          )}
        {bidDetails.getFactorPercentage &&
          renderColumnComponent(
            "Get Factor (%)",
            bidDetails.getFactorPercentage.toString()
          )}
        {renderColumnComponent(
          "Go Get Factor (%)",
          bidDetails.winProbabilityPercentage.toString()
        )}
        {renderColumnComponent(
          "Manual Go Factor (%)",
          bidDetails.manualGoFactorPercentage?.toString()
        )}
        {renderColumnComponent(
          "Manual Get Factor (%)",
          bidDetails.manualGetFactorPercentage?.toString()
        )}
        {renderColumnComponent(
          "Manual Go Get Factor (%)",
          bidDetails.manualGoGetFactorPercentage?.toString()
        )}
        {renderColumnComponent(
          "Go Get Factor Remarks",
          bidDetails.manualGoGetFactorRemarks
        )}
        {renderColumnComponent(
          "Finances (Funding source etc)",
          fundingSource?.name
        )}
        {fundingSource?.name === "Others" &&
          renderColumnComponent(
            "Other Funding Source",
            bidDetails.otherFundingSource && bidDetails.otherFundingSource
          )}
        {renderColumnComponent(
          "Tender expected Date",
          displayDateFormart(bidDetails.tenderExpectedDate)
        )}
        {renderColumnComponent(
          "Award expected Date",
          displayDateFormart(bidDetails.awardExpectedDate)
        )}
        {renderColumnComponent(
          "Project Duration (excluding O&M)",
          bidDetails.projectDuration.toString()
        )}
        {renderColumnComponent(
          "Estimated Time to bid",
          bidDetails.estimatedTimeToBid.toString()
        )}
        {renderColumnComponent(
          "Time to PQ (Days)",
          bidDetails.timeToPQDays?.toString()
        )}
        {renderColumnComponent(
          "O & M part of scope?",
          bidDetails.isOM ? "Yes" : "No"
        )}
        {bidDetails.isOM === true &&
          renderColumnComponent(
            "O&M Duration",
            bidDetails.omduration.toString()
          )}
        {tenderingBandwidth?.name &&
          renderColumnComponent(
            "Tendering bandwidth",
            tenderingBandwidth?.name
          )}
      </Grid>
      <Typography variant="subtitle1">Additional Details</Typography>
      <Divider />
      <Grid container spacing={1}>
        {bidDetails.tenderMaturity &&
          renderColumnComponent("Tender Maturity", bidDetails.tenderMaturity)}
        {renderColumnComponent("Geography", geography?.name)}
        {bidDetails.isLicenseRequired &&
          renderColumnComponent(
            "License Requirement (if international)",
            bidDetails.isLicenseRequired ? "Yes" : "No"
          )}
        {bidDetails.licenseProcurementNextSteps &&
          renderColumnComponent(
            "If required license not available",
            bidDetails.licenseProcurementNextSteps
          )}
        {bidDetails.country &&
          renderColumnComponent("Country", bidDetails.country)}
        {bidDetails.state && renderColumnComponent("State", bidDetails.state)}
        {bidDetails.city && renderColumnComponent("City", bidDetails.city)}
        {bidDetails.location &&
          renderColumnComponent("Location", bidDetails.location)}
        {bidDetails.environmentClearanceStatus &&
          renderColumnComponent(
            "Environment clearance Status",
            bidDetails.environmentClearanceStatus
          )}
        {bidDetails.rowstatus &&
          renderColumnComponent("ROW Status", bidDetails.rowstatus)}
        {renderColumnComponent(
          "Competition Intensity",
          competitionIntensity?.name
        )}

        {renderColumnComponent("Proposal Stage", bidDetails.proposalStage)}

        {renderColumnComponent(
          "Competitors executing work with client",
          bidDetails.competitorsWithClient
        )}
        {renderColumnComponent(
          "Win strategy and value proposition",
          bidDetails.winStrategyAndValueProposition
        )}
        {renderColumnComponent(
          "Country corruption rating (TCPI)",
          bidDetails.countryCorruptionRating
        )}
        {renderColumnComponent(
          "Expected Competitors",
          bidDetails.expectedCompetitors
        )}
        {bidDetails.additionalInformation &&
          renderColumnComponent(
            "Any other relevant information",
            bidDetails.additionalInformation
          )}
      </Grid>
    </Box>
  );
}
